import React from "react"

import CloseIcon from "@mui/icons-material/Close"
import UploadIcon from "@mui/icons-material/Upload"
import {
    Box,
    Button,
    Chip,
    Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton,
    MenuItem,
    Modal,
    Stack,
    TextField,
    Typography
} from "@mui/material"

import { Trans } from "@lingui/macro"

import {
    Asset,
    AssetStatus,
    Document,
    PaginatedResponse,
    SessionResponse,
} from "models"
import { mdiClose } from "@mdi/js"
import Icon from "@mdi/react"
import noImage from "../../../dashboard/dashboard-map/dashboard-map-stores/no-image-found.jpg"
import UploadPhotoModal from "./upload-modal/upload-photo-modal"
import { getChipProps } from "../../../commons/asset-chip-props"
import { translateEnumAssetState } from "../../../../commons/utils"

interface PhotoListProps {
    asset: Asset | null
    sessionData: SessionResponse
    photoData: PaginatedResponse<Document>
    callbackUploadPhoto?: (data: FormData) => void
    deletePhoto?: (photoId: string) => void
    editableStatus: { value: AssetStatus | ""; isEditing: boolean }
    onStatusDoubleClick: () => void
    onStatusChange: (status: AssetStatus) => void
    onStatusKeyDown: (event: React.KeyboardEvent) => void
    onStatusBlur: () => void
    statusOptions: { value: AssetStatus; label: string }[]
}
interface PhotoListState {
    selectedPhoto: Document | null
    openUploadPhotoModal: boolean
    openDeletePhotoModal: boolean
    openImage: boolean
}
export class PhotoList extends React.Component<PhotoListProps, PhotoListState> {

    /*
     * To visualize both layouts (with and w/o uploaded images)
     * you can click on the upload image button to switch
     * */

    constructor(props: PhotoListProps) {
        super(props)

        this.state = {
            selectedPhoto: null,
            openUploadPhotoModal: false,
            openDeletePhotoModal: false,
            openImage: false
        }
    }

    componentDidUpdate(prevProps: Readonly<PhotoListProps>): void {
        if (prevProps.photoData !== this.props.photoData) {
            const items = this.props.photoData.items

            if (items.length === 0) {
                if (this.state.selectedPhoto) {
                    this.setState(() => ({ selectedPhoto: null }))
                }
                return
            }

            if (!this.state.selectedPhoto || !items.find((item) => item.id === this.state.selectedPhoto?.id)) {
                this.setState(() => ({ selectedPhoto: items[0] }))
            }
        }
    }

    public openUploadPhotoModal = () => {
        this.setState(() => ({
            openUploadPhotoModal: true
        }))
    }

    public closeUploadPhotoModal = (data?: FormData) => {
        if (data) {
            this.setState(() => ({
                openUploadPhotoModal: false
            }), () => {
                if (this.props.callbackUploadPhoto) {
                    this.props.callbackUploadPhoto(data)
                }
            })
        }
        else {
            this.setState(() => ({
                openUploadPhotoModal: false
            }))
        }
    }

    public openDeletePhotoModal = (photo: Document) => {
        this.setState(() => ({
            openDeletePhotoModal: true,
            selectedPhoto: photo
        }))
    }

    public closeDeletePhotoModal = (del?: boolean) => {
        if (del) {
            this.setState(() => ({
                openUploadPhotoModal: false
            }), () => {
                if (this.props.deletePhoto && this.state.selectedPhoto) {
                    this.props.deletePhoto(this.state.selectedPhoto.id)
                }
            })
        }
        else {
            this.setState(() => ({
                openUploadPhotoModal: false
            }))
        }

    }

    private showImage = (image: Document) => {
        this.setState(() => ({
            openImage: true,
            selectedPhoto: image
        }))
    }

    private closeImage = () => {
        this.setState(() => ({
            openImage: false,
            selectedPhoto: null
        }))
    }

    render() {
        const userIsBrand = this.props.sessionData.accountType === "BRAND"
        const canManagePhotos = !!this.props.deletePhoto && !!this.props.callbackUploadPhoto && !userIsBrand
        const photos = this.props.photoData.items ?? []
        const activePhoto = this.state.selectedPhoto ?? photos[0] ?? null
        const activePhotoSrc = activePhoto && this.props.asset
            ? `/api/asset/${this.props.asset.id}/document/${activePhoto.id}`
            : noImage
        const statusLabel = this.props.asset?.characteristics?.status
            ? translateEnumAssetState(this.props.asset.characteristics.status)
            : null
        const statusChipProps = statusLabel ? getChipProps(statusLabel) : null
        const isStatusEditing = this.props.editableStatus?.isEditing
        const statusFieldValue = this.props.editableStatus?.value || this.props.asset?.characteristics?.status || ""

        return (
            <Box component="div">
                <Box
                    sx={{
                        position: "relative",
                        borderRadius: 2,
                        overflow: "hidden",
                        backgroundColor: "background.default",
                    }}
                >
                    <Box
                        component="img"
                        src={activePhotoSrc}
                        alt={activePhoto?.name ?? "Immagine immobile"}
                        loading="lazy"
                        onClick={() => activePhoto && this.showImage(activePhoto)}
                        sx={{
                            width: "100%",
                            height: { xs: 220, md: 320 },
                            objectFit: "cover",
                            display: "block",
                            cursor: activePhoto ? "pointer" : "default",
                        }}
                    />
                    {statusChipProps && !isStatusEditing && (
                        <Chip
                            {...statusChipProps}
                            size="small"
                            onDoubleClick={this.props.onStatusDoubleClick}
                            sx={{
                                ...(statusChipProps.sx ?? {}),
                                position: "absolute",
                                top: 12,
                                left: 12,
                                cursor: "pointer",
                            }}
                        />
                    )}
                    {statusChipProps && isStatusEditing && (
                        <TextField
                            select
                            size="small"
                            autoFocus
                            value={statusFieldValue}
                            onChange={(event) => this.props.onStatusChange(event.target.value as AssetStatus)}
                            onKeyDown={this.props.onStatusKeyDown}
                            onBlur={this.props.onStatusBlur}
                            sx={{
                                position: "absolute",
                                top: 12,
                                left: 12,
                                minWidth: 190,
                                backgroundColor: "background.paper",
                                borderRadius: 1,
                            }}
                        >
                            {this.props.statusOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, overflowX: "auto", pb: 0.5 }}>
                    {photos.map((item) => {
                        const isActive = activePhoto?.id === item.id
                        const thumbnailSrc = this.props.asset
                            ? `/api/asset/${this.props.asset.id}/document/${item.id}`
                            : noImage

                        return (
                            <Box
                                key={item.id}
                                sx={{
                                    position: "relative",
                                    flex: "0 0 auto",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                    border: isActive ? "2px solid" : "1px solid",
                                    borderColor: isActive ? "secondary.main" : "divider",
                                    cursor: "pointer",
                                }}
                                onClick={() => this.setState(() => ({ selectedPhoto: item }))}
                            >
                                <Box
                                    component="img"
                                    src={thumbnailSrc}
                                    alt={item.name}
                                    loading="lazy"
                                    sx={{
                                        width: 88,
                                        height: 72,
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                />
                                {canManagePhotos && (
                                    <IconButton
                                        size="small"
                                        onClick={(event) => {
                                            event.stopPropagation()
                                            this.openDeletePhotoModal(item)
                                        }}
                                        sx={{
                                            position: "absolute",
                                            top: 2,
                                            right: 2,
                                            backgroundColor: "rgba(0,0,0,0.6)",
                                            color: "white",
                                            "&:hover": {
                                                backgroundColor: "rgba(0,0,0,0.7)",
                                            },
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        )
                    })}
                    {canManagePhotos && (
                        <Button
                            startIcon={<UploadIcon />}
                            variant="outlined"
                            size="small"
                            onClick={this.openUploadPhotoModal}
                            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                        >
                            <Trans id={"fm-upload-button"}>Upload</Trans>
                        </Button>
                    )}
                </Stack>
                {photos.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        <Trans id={"asset.photo-list.no-image"}>Nessuna immagine disponibile.</Trans>
                    </Typography>
                )}
                {
                    this.state.openUploadPhotoModal &&
                    <UploadPhotoModal open={this.state.openUploadPhotoModal} onClose={this.closeUploadPhotoModal} callbackUploadPhoto={this.closeUploadPhotoModal} />
                }
                {
                    this.state.openDeletePhotoModal &&
                    <Dialog open={this.state.openDeletePhotoModal} onClose={() => (this.closeDeletePhotoModal)}>
                        <DialogTitle>
                            <Trans id={"asset.photo-list.delete-title"}>
                                Eliminazione immagine
                            </Trans>
                        </DialogTitle>
                        <DialogContent>
                            <Trans id={"asset.photo-list.delete-body"}>
                                Sei sicuro di voler eliminare l'immagine? L'operazione non sarà reversibile.
                            </Trans>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => this.closeDeletePhotoModal()}
                                variant={"contained"}
                                color="neutral"
                                sx={{
                                    textTransform: "none",
                                    mr: 1,
                                    width: 100
                                }}
                                disableElevation>
                                <Trans id={"cbm-close-button"}>
                                    Annulla
                                </Trans>
                            </Button>
                            <Button onClick={() => (this.closeDeletePhotoModal(true))}
                                variant="contained"
                                sx={{
                                    backgroundColor: 'secondary.main',
                                    color: 'white',
                                    mr: 1,
                                    textTransform: "none",
                                    width: 100
                                }}
                            >
                                <Trans id={"fm-confirm-button"}>Conferma</Trans>
                            </Button>
                        </DialogActions>
                    </Dialog>
                }
                {
                    this.state.openImage && this.props.asset && this.state.selectedPhoto &&
                    <Modal open={this.state.openImage}
                        onClose={this.closeImage}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Box sx={{ p: 0, position: "relative" }}>
                            <Box sx={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                width: "100%",
                                background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, ' +
                                    'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)'
                            }}
                                textAlign="right"
                            >
                                <IconButton onClick={this.closeImage}>
                                    <Icon path={mdiClose} size={1.5} color={"white"} />
                                </IconButton>
                            </Box>
                            <img
                                src={`/api/asset/${this.props.asset.id}/document/${this.state.selectedPhoto.id}`}
                                alt={this.state.selectedPhoto.name}
                                loading="lazy"
                            />
                        </Box>
                    </Modal>
                }
            </Box>
        )
    }
}
